// Custom Email Service for The Frosting Room
// Uses Firebase Functions backend for email sending

import { EmailData, EmailResponse } from './emailService';

// Get Firebase Function URL from environment variable
const getEmailApiUrl = (): string => {
  const apiUrl = process.env.REACT_APP_EMAIL_API_URL;
  if (!apiUrl) {
    throw new Error('REACT_APP_EMAIL_API_URL environment variable is not set');
  }
  return apiUrl;
};

/**
 * Send email using Firebase Functions backend
 */
export const sendEmail = async (emailData: EmailData): Promise<EmailResponse> => {
  try {
    const apiUrl = getEmailApiUrl();
    
    console.log('📧 Custom Email Service - Sending email:', {
      to: emailData.to,
      subject: emailData.subject,
      hasHtml: !!emailData.html,
      hasText: !!emailData.text,
      replyTo: emailData.replyTo
    });

    const response = await fetch(apiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        to: emailData.to,
        cc: emailData.cc,
        bcc: emailData.bcc,
        subject: emailData.subject,
        html: emailData.html,
        text: emailData.text,
        replyTo: emailData.replyTo,
        from: emailData.from
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      console.log('✅ Custom Email Service - Email sent successfully:', result.messageId);
      return {
        success: true,
        messageId: result.messageId
      };
    } else {
      throw new Error(result.error || 'Email sending failed');
    }
    
  } catch (error) {
    console.error('❌ Custom Email Service - Error sending email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};

/**
 * Send contact form email using Firebase Functions backend
 */
export const sendContactEmail = async (formData: {
  name: string;
  email: string;
  phone?: string;
  message: string;
}): Promise<EmailResponse> => {
  try {
    // Get the contact email function URL
    const contactApiUrl = process.env.REACT_APP_CONTACT_EMAIL_API_URL || 
      (process.env.REACT_APP_EMAIL_API_URL 
        ? process.env.REACT_APP_EMAIL_API_URL.replace('/send-email', '/send-contact-email')
        : null);
    
    if (!contactApiUrl) {
      throw new Error('REACT_APP_CONTACT_EMAIL_API_URL or REACT_APP_EMAIL_API_URL environment variable is not set');
    }
    
    console.log('📧 Custom Email Service - Sending contact form email...');

    const response = await fetch(contactApiUrl, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        message: formData.message
      })
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ error: 'Unknown error' }));
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
    }

    const result = await response.json();
    
    if (result.success) {
      console.log('✅ Custom Email Service - Contact form email sent successfully:', result.messageId);
      return {
        success: true,
        messageId: result.messageId
      };
    } else {
      throw new Error(result.error || 'Contact form email sending failed');
    }
    
  } catch (error) {
    console.error('❌ Custom Email Service - Error sending contact form email:', error);
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Unknown error occurred'
    };
  }
};
